import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { defaultMapBoundsPadding } from '../order-status-tracking/order-status-tracking.component';
import { OrdersService, QuoteResponse, TrackingInfo } from '@evermile/customer-tracking';
import { MapMarker, MapMarkerType } from '../../../utils/mapUtils';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { format } from 'date-fns';
import { Address } from '@evermile/customer-tracking/model/address';
import { EditOrderService } from '../../../services/edit-order.service';
import { DateProposals } from '@evermile/customer-tracking/model/dateProposals';
import { Subscription } from 'rxjs';
import { GtmTracking } from '../../../services/gtm.service';

@Component({
  selector: 'common-change-address-form',
  templateUrl: './change-address-form.component.html',
  styleUrls: ['./change-address-form.component.scss'],
})
export class ChangeAddressFormComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  @Input() order!: TrackingInfo;
  @Input() index!: number;
  @Input() brandColor?: string;
  @Input() textColor?: string;
  dropoffMarker?: MapMarker | any;
  addressSelected?: Address;
  loading = false;
  showChangeTimeMenu = false;
  orderWithNewDestination?: TrackingInfo;
  changeTimeNote = 'The new address you selected requires a change in the delivery time.';
  lastQuoteResponse?: QuoteResponse;
  showConfirmationModal = false;
  showAddressError = false;
  //Subs
  updateOrderSub?: Subscription;
  indexChangeSub?: Subscription;

  protected readonly defaultMapBoundsPadding = defaultMapBoundsPadding;

  constructor(
    private fb: FormBuilder,
    private editOrderService: EditOrderService,
    private gtm: GtmTracking,
    private apiEditOrderService: OrdersService,
  ) {}

  ngOnInit() {
    this.setForm();
    this.setInitialMapMarker();
    this.updateOrderSub = this.editOrderService.orderWasUpdated$.subscribe(() => {
      this.loading = false;
    });
    setTimeout(() => {
      this.editOrderService.hideCloseButton.emit(true);
    }, 500);
    this.indexChangeSub = this.editOrderService.afterIndexChange$.subscribe((val) => {
      if (val === this.index) {
        this.clearPrevData();
      }
    });
  }

  setInitialMapMarker() {
    this.dropoffMarker = {
      id: 'dropoff',
      type: MapMarkerType.dropoff,
      coordinate: this.order.destinationAddress.geoLocation!,
      infoWindowText:
        '<div id="window-info"><span class="title">Current delivery address:</span><br><span class="content">' +
        this.order.destinationAddress.addressLine1 +
        '</span></div>',
      disableInfoWindowOpenClose: true,
    };
  }

  clearPrevData() {
    this.setInitialMapMarker();
    this.addressSelected = undefined;
  }

  onConfirmClose() {
    this.editOrderService.closeMenu.emit();
  }

  onClose() {
    if (this.addressSelected) {
      this.openConfirmationModal();
    } else {
      this.editOrderService.closeMenu.emit();
    }
  }

  closeTimeMenu() {
    this.showChangeTimeMenu = false;
    this.clearPrevData();
  }

  onCancelClose() {
    this.showConfirmationModal = false;
  }

  openConfirmationModal() {
    this.showConfirmationModal = true;
  }

  ngOnDestroy() {
    this.updateOrderSub?.unsubscribe();
    this.indexChangeSub?.unsubscribe();
  }

  submitNewDropoff() {
    this.loading = true;
    const date = this.order?.trackingInfo?.dropoffWindowStart || '';
    this.apiEditOrderService
      .orderEditQuotePost({
        address: {
          addressLine1: this.addressSelected!.addressLine1,
          addressLine2: this.form.get('addressLine2')?.value,
          city: this.addressSelected!.city,
          postalCode: this.addressSelected!.postalCode,
          type: this.addressSelected!.type,
          geoLocation: this.addressSelected!.geoLocation,
        },
        date: format(new Date(date), 'y-MM-dd'),
      })
      .subscribe((res) => {
        this.lastQuoteResponse = res;
        const sameTimeProposalId = this.getSameTimeProposalId(res.dateProposals);
        if (sameTimeProposalId) {
          this.editOrderService.submitEditOrder({
            proposalId: sameTimeProposalId,
          });
        } else {
          this.orderWithNewDestination = {
            ...this.order,
            destinationAddress: this.addressSelected!,
          };
          if (!this.editOrderService.areThereValidProposals(res.dateProposals)) {
            this.editOrderService.showErrorScreen.emit();
            this.gtm.invalidForm('Edit order post quote', res.dateProposals, {});
          } else {
            this.showChangeTimeMenu = true;
          }
          this.loading = false;
        }
      });
  }

  getSameTimeProposalId(proposals: DateProposals[]) {
    const originalDropoffStart = this.order?.trackingInfo?.dropoffWindowStart;
    const originalDropoffEnd = this.order?.trackingInfo?.dropoffWindowEnd;
    let timeProposal = '';
    proposals?.forEach((p) => {
      if (
        p.proposals.find(
          (p) =>
            p.proposal?.estimatedDropoff?.start === originalDropoffStart &&
            p.proposal?.estimatedDropoff?.end === originalDropoffEnd,
        )
      ) {
        timeProposal =
          p.proposals.find(
            (p) =>
              p.proposal?.estimatedDropoff?.start === originalDropoffStart &&
              p.proposal?.estimatedDropoff?.end === originalDropoffEnd,
          )?.proposal?.id || '';
      }
    });
    return timeProposal;
  }

  onAddressSelected(address: Address) {
    this.showAddressError = false;
    this.addressSelected = address;
    this.dropoffMarker = {
      ...this.dropoffMarker,
      coordinate: {
        lat: address!.geoLocation!.lat,
        lng: address!.geoLocation!.lng,
      },
      infoWindowText:
        '<div id="window-info"><span class="title">New delivery address:</span><br><span class="content">' +
        address.addressLine1 +
        '</span></div>',
    };
  }

  setForm() {
    this.form = this.fb.group({
      address: new FormControl('', Validators.required),
      addressLine2: new FormControl(''),
    });
  }
}
